import React, { useState, useRef } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { AdminAPI, resolveImage } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

/**
 * Reusable image uploader for admin forms.
 * - Accepts a current image URL (can be /api/files/{id} or external URL)
 * - Uploads via POST /admin/upload; returns the new URL via onChange
 */
const ImageUpload = ({ value, onChange, label = 'Image', testId = 'image-upload', accept = 'image/*' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Fichier trop volumineux', description: 'Maximum 5 Mo' });
      return;
    }
    setUploading(true);
    try {
      const { url } = await AdminAPI.upload(file);
      onChange(url);
      toast({ title: '✅ Image téléversée' });
    } catch (e) {
      toast({ title: 'Échec', description: e?.response?.data?.detail || 'Upload impossible' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && <label className="text-sm text-[#2D2A26] mb-1.5 block">{label}</label>}
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-[#FAF7F2] border border-[#E8E0D0] flex-shrink-0">
            <img src={resolveImage(value)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 hover:bg-white text-[#C75B27] flex items-center justify-center shadow-sm"
              title="Retirer"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="w-20 h-20 rounded-lg bg-[#FAF7F2] border-2 border-dashed border-[#E8E0D0] flex items-center justify-center flex-shrink-0">
            <Upload className="w-5 h-5 text-[#C9BFAE]" />
          </div>
        )}
        <div className="flex-1">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`${testId}-input`}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            data-testid={`${testId}-button`}
            className="px-3 py-2 text-sm rounded-lg border border-[#E8E0D0] hover:bg-[#FAF7F2] text-[#1F4D3F] font-medium inline-flex items-center gap-1.5"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? 'Téléversement...' : value ? 'Remplacer' : 'Choisir un fichier'}
          </button>
          <div className="text-xs text-[#7A6F5C] mt-1">JPG, PNG, WEBP · max 5 Mo</div>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
